import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { MailMessageListItem, SmtpAccount } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { ProjectPickerEmpty } from '@/features/projects/ProjectPickerEmpty';
import { ProjectWorkspaceHeader } from '@/features/projects/ProjectWorkspaceHeader';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/Field';
import leadStyles from '@/features/leads/Leads.module.css';
import projectStyles from './ProjectsPage.module.css';
import styles from './InboxPage.module.css';
import { ResizableWorkspace } from '@/components/workspace/ResizableWorkspace';
import { InboxMessageDetail } from '@/features/mail/InboxMessageDetail';

type MailMessageDetail = MailMessageListItem & {
  body_text: string | null;
  body_html: string | null;
  lead_id: string | null;
  is_read: boolean;
  status?: string;
  reply_classification?: import('@/features/mail/ReplyClassificationBadge').ReplyClassificationSummary | null;
};

export function InboxPage() {
  const { selectedProject, selectedProjectId, setSelectedProjectId } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();
  const [folder, setFolder] = useState<'inbox' | 'sent' | 'archived' | 'drafts'>('inbox');
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [linkedOnly, setLinkedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [linkLeadId, setLinkLeadId] = useState('');
  const [mailboxId, setMailboxId] = useState('');
  const [syncBackfill, setSyncBackfill] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  useEffect(() => {
    setMailboxId('');
    setSelectedId(null);
    setReplyOpen(false);
  }, [projectId]);

  useEffect(() => {
    setReplyOpen(false);
  }, [selectedId]);

  const accountsQ = useQuery({
    queryKey: ['smtp-accounts', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ accounts: SmtpAccount[] }>(`/api/smtp-accounts?project_id=${projectId}`),
  });

  const queryString = useMemo(() => {
    const direction =
      folder === 'sent' || folder === 'drafts'
        ? 'outbound'
        : folder === 'inbox' || unmatchedOnly
          ? 'inbound'
          : 'all';
    const p = new URLSearchParams({
      project_id: projectId ?? '',
      folder,
      direction,
    });
    if (mailboxId === '__brevo__') p.set('brevo_only', 'true');
    else if (mailboxId) p.set('smtp_account_id', mailboxId);
    if (unmatchedOnly) p.set('unmatched_only', 'true');
    if (linkedOnly) p.set('linked_only', 'true');
    if (search.trim()) p.set('q', search.trim());
    return p.toString();
  }, [projectId, folder, mailboxId, unmatchedOnly, linkedOnly, search]);

  const delQ = useQuery({
    queryKey: ['mail-deliverability', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ unmatched_inbound: number }>(
        `/api/mail/deliverability?project_id=${projectId}`,
      ),
  });

  const selectedMailbox = useMemo(
    () => accountsQ.data?.accounts.find((a) => a.id === mailboxId),
    [accountsQ.data?.accounts, mailboxId],
  );

  const inboxQ = useQuery({
    queryKey: ['mail-inbox', queryString],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ messages: MailMessageListItem[]; total: number }>(
        `/api/mail/inbox?${queryString}`,
      ),
  });

  const detailQ = useQuery({
    queryKey: ['mail-message', selectedId],
    enabled: Boolean(selectedId),
    queryFn: () => apiFetch<{ message: MailMessageDetail }>(`/api/mail/messages/${selectedId}`),
  });

  const patchMsg = useMutation({
    mutationFn: (args: { id: string; body: Record<string, unknown> }) =>
      apiFetch(`/api/mail/messages/${args.id}`, {
        method: 'PATCH',
        body: JSON.stringify(args.body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mail-inbox'] });
      void qc.invalidateQueries({ queryKey: ['mail-message'] });
    },
  });

  const syncImap = useMutation({
    mutationFn: (args: { accountId: string; reset_inbox?: boolean; reset_sent?: boolean }) =>
      apiFetch(`/api/smtp-accounts/${args.accountId}/sync-imap`, {
        method: 'POST',
        body: JSON.stringify({
          reset_inbox: args.reset_inbox,
          reset_sent: args.reset_sent,
        }),
      }),
    onSuccess: () => {
      window.setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['mail-inbox'] });
        void qc.invalidateQueries({ queryKey: ['smtp-accounts', projectId] });
      }, 4000);
    },
  });

  const bulk = useMutation({
    mutationFn: (body: { message_ids: string[]; is_read?: boolean; archive?: boolean }) =>
      apiFetch('/api/mail/messages/bulk', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, ...body }),
      }),
    onSuccess: () => {
      setSelectedIds(new Set());
      void qc.invalidateQueries({ queryKey: ['mail-inbox'] });
    },
  });

  return (
    <ProjectGate pageTitle="Inbox">
      {!selectedProjectId ? (
        <ProjectPickerEmpty
          title="Inbox"
          description="Unified inbox is per project (IMAP-sync via mailboxen). Kies de werkruimte waarvan je berichten wilt lezen, matchen en archiveren."
          cardHint="Inbox & mailthreads voor dit project"
        />
      ) : inboxQ.isLoading ? (
        <LoadingState />
      ) : (
        <div className={styles.page}>
          <ProjectWorkspaceHeader
            title="Inbox"
            subtitle="Inbox = inkomend (IMAP INBOX). Verzonden = uitgaande campagnes/tests. Sync elke ~2 min of handmatig IMAP sync."
            projectName={selectedProject?.name ?? ''}
            onSwitchProject={() => setSelectedProjectId(null)}
          />

          <div className={styles.toolbar}>
            <select
              className={`${leadStyles.filterInput} ${styles.mailboxSelect}`}
              value={mailboxId}
              onChange={(e) => {
                setMailboxId(e.target.value);
                setSelectedId(null);
              }}
              aria-label="Mailbox"
            >
              <option value="">Alle mailboxen</option>
              <option value="__brevo__">Brevo (transactional log)</option>
              {(accountsQ.data?.accounts ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} ({a.from_email})
                  {!a.has_imap ? ' — geen IMAP' : ''}
                </option>
              ))}
            </select>
            <Button
              variant={folder === 'inbox' ? 'primary' : 'secondary'}
              onClick={() => setFolder('inbox')}
            >
              Inbox
            </Button>
            <Button
              variant={folder === 'sent' ? 'primary' : 'secondary'}
              onClick={() => setFolder('sent')}
            >
              Verzonden
            </Button>
            <Button
              variant={folder === 'archived' ? 'primary' : 'secondary'}
              onClick={() => setFolder('archived')}
            >
              Archief
            </Button>
            <Button
              variant={folder === 'drafts' ? 'primary' : 'secondary'}
              onClick={() => {
                setFolder('drafts');
                setUnmatchedOnly(false);
                setLinkedOnly(false);
              }}
            >
              Concepten (HITL)
            </Button>
            <Button
              variant={linkedOnly ? 'primary' : 'ghost'}
              onClick={() => {
                setLinkedOnly((v) => !v);
                if (!linkedOnly) setUnmatchedOnly(false);
              }}
            >
              Campagne-reacties
            </Button>
            <Button
              variant={unmatchedOnly ? 'primary' : 'ghost'}
              onClick={() => {
                setUnmatchedOnly((v) => !v);
                if (!unmatchedOnly) setLinkedOnly(false);
              }}
            >
              Unmatched
              {delQ.data?.unmatched_inbound
                ? ` (${delQ.data.unmatched_inbound})`
                : ''}
            </Button>
            <div className={styles.toolbarSearch}>
              <TextInput
                placeholder="Zoeken…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {mailboxId && selectedMailbox?.has_imap ? (
              <>
                <label className={styles.syncBackfill}>
                  <input
                    type="checkbox"
                    checked={syncBackfill}
                    onChange={(e) => setSyncBackfill(e.target.checked)}
                  />
                  Ook laatste ~500 berichten opnieuw
                </label>
                <Button
                  variant="secondary"
                  disabled={syncImap.isPending}
                  onClick={() =>
                    syncImap.mutate({
                      accountId: mailboxId,
                      reset_inbox: syncBackfill,
                      reset_sent: syncBackfill,
                    })
                  }
                >
                  {syncImap.isPending ? 'Sync…' : 'IMAP sync'}
                </Button>
              </>
            ) : null}
          </div>
          {selectedMailbox?.has_imap && selectedMailbox.imap_last_sync_at ? (
            <p className={projectStyles.mutedSmall}>
              Laatste IMAP-sync {selectedMailbox.label}:{' '}
              {new Date(selectedMailbox.imap_last_sync_at).toLocaleString('nl-NL')}
              {selectedMailbox.last_error ? ` — fout: ${selectedMailbox.last_error}` : ''}
            </p>
          ) : null}
          {syncImap.isSuccess ? (
            <p className={projectStyles.mutedSmall}>
              Sync gestart; ververs over enkele seconden als er nieuwe berichten zijn.
            </p>
          ) : null}

          {selectedMailbox && !selectedMailbox.has_imap ? (
            <p className={projectStyles.mutedSmall}>
              Deze mailbox heeft geen IMAP — je ziet vooral uitgaande mail (campagnes/test). Stel IMAP in via{' '}
              <Link to="/app/mailboxes">Mailboxen</Link> om inkomende berichten te synchroniseren.
            </p>
          ) : null}
          {!accountsQ.isLoading && (accountsQ.data?.accounts.length ?? 0) === 0 ? (
            <p className={projectStyles.mutedSmall}>
              Geen mailboxen voor dit project. Voeg er een toe onder{' '}
              <Link to="/app/mailboxes">Mailboxen</Link>.
            </p>
          ) : null}

          {selectedIds.size ? (
            <div className={styles.bulkBar}>
              <span>{selectedIds.size} geselecteerd</span>
              <Button
                variant="secondary"
                onClick={() => bulk.mutate({ message_ids: [...selectedIds], is_read: true })}
              >
                Gelezen
              </Button>
              <Button
                variant="secondary"
                onClick={() => bulk.mutate({ message_ids: [...selectedIds], archive: true })}
              >
                Archiveer
              </Button>
            </div>
          ) : null}

          <ResizableWorkspace
            storageKey="mh-inbox-detail-width"
            detailOpen={Boolean(selectedId)}
            onDetailDismiss={() => setSelectedId(null)}
            main={
            <div className={leadStyles.tableWrap}>
              <table className={leadStyles.table}>
                <thead>
                  <tr>
                    <th />
                    <th>Onderwerp</th>
                    <th>Mailbox</th>
                    <th>Lead</th>
                  </tr>
                </thead>
                <tbody>
                  {(inboxQ.data?.messages ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className={projectStyles.mutedSmall} style={{ padding: '1rem' }}>
                        {folder === 'inbox'
                          ? linkedOnly
                            ? 'Nog geen gekoppelde campagne-reacties. Antwoorden komen binnen via IMAP op support@ploko.nl; koppel unmatched berichten handmatig indien nodig.'
                            : 'Geen inkomende berichten. Reageer op een campagnemail naar het mailbox-adres (niet alleen vanuit Outlook zonder sync), daarna IMAP sync met backfill.'
                          : 'Geen berichten in deze map.'}
                      </td>
                    </tr>
                  ) : null}
                  {(inboxQ.data?.messages ?? []).map((m) => (
                    <tr
                      key={m.id}
                      className={m.id === selectedId ? leadStyles.rowActive : undefined}
                      onClick={() => {
                        setSelectedId(m.id);
                        if (!m.is_read && m.direction === 'inbound') {
                          patchMsg.mutate({ id: m.id, body: { is_read: true } });
                        }
                      }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(m.id)}
                          onChange={() => {
                            const next = new Set(selectedIds);
                            if (next.has(m.id)) next.delete(m.id);
                            else next.add(m.id);
                            setSelectedIds(next);
                          }}
                        />
                      </td>
                      <td>
                        {!m.is_read && m.direction === 'inbound' ? '● ' : ''}
                        {m.subject ?? '—'}
                      </td>
                      <td className={styles.mailboxCell}>
                        {m.mailbox_label ?? m.mailbox_from_email ?? '—'}
                      </td>
                      <td>{m.lead_name ?? m.lead_email ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            }
            detail={
              selectedId ? (
                detailQ.isLoading ? (
                  <LoadingState label="Bericht laden…" />
                ) : detailQ.data?.message ? (
                  <InboxMessageDetail
                    message={detailQ.data.message}
                    linkLeadId={linkLeadId}
                    onLinkLeadIdChange={setLinkLeadId}
                    replyOpen={replyOpen}
                    onReplyOpenChange={setReplyOpen}
                    onClose={() => setSelectedId(null)}
                    onDraftResolved={() => {
                      void qc.invalidateQueries({ queryKey: ['mail-inbox'] });
                      void qc.invalidateQueries({ queryKey: ['mail-message'] });
                    }}
                    patchMsg={patchMsg}
                  />
                ) : null
              ) : null
            }
            resizerAriaLabel="Breedte berichtpaneel aanpassen"
          />
        </div>
      )}
    </ProjectGate>
  );
}
